import { toPng } from "html-to-image";
import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import HorizontalStackedBarChart from "../../../components/chart/HorizontalStackedBarChart";
import StandardChartContainer from "../../../components/container/StandardChartContainer";
import { useTheme } from "@mui/material";
import { GRAPH_PALETTE } from "../../../theme/style/graphChartPalette";

const FLOW_KEYS = ["recycled", "reuse", "remanufactured", "biological", "virgin"];

const InputMaterialChart = forwardRef(({ circularities, height }, parentRef) => {
  const { t } = useTranslation();
  const ref = useRef();
  const dataKeys = ["Recycled content (%)", "Reuse (%)", "Remanufactured (%)", "Biological materials (%)", "Virgin Content (%)"];
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // One circularity flow token per data key, in the same order as dataKeys.
  const getColor = (index) => {
    const flow = GRAPH_PALETTE.circularityFlows[FLOW_KEYS[index % FLOW_KEYS.length]];
    return isDark ? flow.dark : flow.light;
  };
  useImperativeHandle(parentRef, () => ({
    getScreenshot: async () => {
      if (ref.current) {
        return await toPng(ref.current, { cacheBust: true, backgroundColor: "#ffffff" });
      }
      return null;
    },
  }));

  const inputData = useMemo(() => {
    const data = (circularities || []).map((item) => {
      const desc = item.description || item.name || t("Unknown Material");
      return {
        name: desc,
        label: desc,
        "Recycled content (%)": parseFloat(item.im_recycled_content) || 0,
        "Reuse (%)": parseFloat(item.im_reuse) || 0,
        "Remanufactured (%)": parseFloat(item.im_remanufactured) || 0,
        "Biological materials (%)": parseFloat(item.im_bio_materials) || 0,
        "Virgin Content (%)": parseFloat(item.im_virgin_content) || 0,
      };
    });
    return data;
  }, [circularities]);

  return (
    <StandardChartContainer ref={ref}>
      <HorizontalStackedBarChart
        title={"Input Materials"}
        height={height}
        data={inputData}
        dataKeys={dataKeys}
        getColor={getColor}
        showLegend
        xAxisLabel="Percentage (%)"
        yAxisLabel="Materials"
        hideDownload
      />
    </StandardChartContainer>
  );
});

export default InputMaterialChart;
